import { supabase } from '@/integrations/supabase/client';

type TipoAcaoHistorico = 
  | 'ATRIBUIDO' 
  | 'DEVOLUCAO_MANUAL'
  | 'CONTATO_ENCONTRADO'
  | 'CONTATO_ATUALIZADO'
  | 'PROPOSTA_ENVIADA'
  | 'RETORNO_ENVIADO'
  | 'FECHAMENTO_ENVIADO'
  | 'STATUS_NEGOCIANDO'
  | 'CONVERTIDO'
  | 'NEGADO'
  | 'CARTEIRIZADO'
  | 'PRAZO_EXPIRADO'
  | 'PRAZO_CONTATO_EXPIRADO'
  | 'FATURA_SOLICITADA'
  | 'FATURA_RECEBIDA'
  | 'LIGACAO_REALIZADA'
  | 'LIGACAO_TENTADA'
  | 'PROPOSTA_VALORES_SALVOS'
  | 'ATUALIZADO_IMPORTACAO'
  | 'IMPORTADO'
  | 'OBSERVACAO_MANUAL'
  | 'TENTANDO_CONTATO'
  | 'REATIVADO'
  | 'CRIADO_MANUAL'
  | 'APRESENTACAO_ENVIADA';

interface RegistrarHistoricoParams {
  leadId: string;
  acao: TipoAcaoHistorico;
  usuarioId?: string;
  dados?: Record<string, any>;
}

/**
 * Serviço centralizado para registrar ações no histórico de leads
 */
export async function registrarHistoricoLead({
  leadId,
  acao,
  usuarioId,
  dados,
}: RegistrarHistoricoParams): Promise<void> {
  try {
    const { error } = await supabase
      .from('leads_carteira_historico')
      .insert({
        lead_id: leadId,
        acao,
        usuario_id: usuarioId || null,
        dados: dados || null,
      });

    if (error) {
      console.error('Erro ao registrar histórico:', error);
    }
  } catch (err) {
    console.error('Exceção ao registrar histórico:', err);
  }
}

/**
 * Registrar proposta enviada
 */
export async function registrarPropostaEnviada(
  leadId: string,
  usuarioId: string,
  dados?: { gb_atual?: number; gb_novo?: number; economia_mes?: number; telefone?: string }
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'PROPOSTA_ENVIADA',
    usuarioId,
    dados,
  });
}

/**
 * Registrar fatura solicitada
 */
export async function registrarFaturaSolicitada(
  leadId: string,
  usuarioId: string,
  dados?: { linha_plano?: string; cnpj?: string }
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'FATURA_SOLICITADA',
    usuarioId,
    dados,
  });
}

/**
 * Registrar fatura recebida
 */
export async function registrarFaturaRecebida(
  leadId: string,
  dados?: { linha_plano?: string }
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'FATURA_RECEBIDA',
    dados,
  });
}

/**
 * Registrar ligação realizada
 */
export async function registrarTentativaLigacao(
  leadId: string,
  usuarioId: string,
  numero: string
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'LIGACAO_TENTADA',
    usuarioId,
    dados: { numero, manual: true, origem: 'softphone' },
  });
}

export const registrarLigacaoRealizada = registrarTentativaLigacao;

/**
 * Registrar valores de proposta salvos
 */
export async function registrarValoresPropostaSalvos(
  leadId: string,
  usuarioId: string,
  dados: { gb_atual?: number; gb_novo?: number; economia_mes?: number }
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'PROPOSTA_VALORES_SALVOS',
    usuarioId,
    dados,
  });
}

/**
 * Registrar mudança para status negociando
 */
export async function registrarStatusNegociando(
  leadId: string,
  usuarioId: string
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'STATUS_NEGOCIANDO',
    usuarioId,
  });
}

/**
 * Registrar lead negado
 */
export async function registrarLeadNegado(
  _leadId: string,
  _usuarioId: string,
  _motivo?: string
): Promise<void> {
  // A timeline do NEGADO agora é gravada pelo TRIGGER do banco (trg_negado_timeline),
  // blindada p/ NUNCA se perder (qualquer caminho registra). No-op aqui p/ não duplicar.
  return;
}

/**
 * Registrar mensagem de retorno enviada
 */
export async function registrarRetornoEnviado(
  leadId: string,
  usuarioId: string
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'RETORNO_ENVIADO',
    usuarioId,
  });
}

/**
 * Registrar mensagem de fechamento enviada
 */
export async function registrarFechamentoEnviado(
  leadId: string,
  usuarioId: string
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'FECHAMENTO_ENVIADO',
    usuarioId,
  });
}

/**
 * Registrar reativação de lead negado
 */
export async function registrarReativacao(
  leadId: string,
  usuarioId: string,
  novoStatus: string,
  consultorId?: string
): Promise<void> {
  return registrarHistoricoLead({
    leadId,
    acao: 'REATIVADO',
    usuarioId,
    dados: {
      status_novo: novoStatus,
      consultor_novo: consultorId || null,
    },
  });
}
